import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  styled,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
} from "@mui/material";
import {
  riskAnalysisService,
  PayorContractAnalysisResponse,
  PayorContractRequestError,
  sortPayorContractFilesForAnalysis,
  type EmployeeAgreementFileResult,
} from "@services/riskAnalysis";
import { buildMergedExtractionFileResults } from "@utils/extractionProgress";
import { ExtractionProgressTable } from "./ExtractionProgressTable";
import { PayorContractResults } from "./PayorContractResults";
import { getSelectedFacilityFromSession } from "@utils/facilityUtils";
import { getSelectedCountryCode } from "@utils/countryUtils";
import {
  getAviationProfileLabel,
  type AviationProfileFacilityLike,
} from "@utils/aviationFacilityProfile";
import { toast } from "react-toastify";
import { continuousComplianceService } from "@services/continuousCompliance";
import { getCurrentUserId } from "@utils/userUtils";
import { formatDateTime } from "@utils/formatUtils";

/** Match Risk Analysis upload accordions (`RiskAnalysis.tsx` StyledAccordion). */
const PayorSectionAccordion = styled(Accordion)({
  marginBottom: "8px",
  borderRadius: "8px !important",
  border: "1px solid #e0e0e0",
  boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
  "&:before": {
    display: "none",
  },
  "&.Mui-expanded": {
    margin: "0 0 8px 0",
  },
  "& .accordion-title": {
    color: "#262626",
    transition: "color 0.3s ease",
  },
  "&.Mui-expanded .accordion-title": {
    color: "#065B70",
  },
  "& .MuiAccordionSummary-root": {
    alignItems: "center",
  },
  "& .MuiAccordionSummary-content": {
    alignItems: "center",
    flexWrap: "nowrap",
  },
  "& .MuiAccordionDetails-root": { paddingTop: "0px" },
});

const PayorSectionTitle = styled(Typography)({
  fontSize: "16px",
  fontWeight: 600,
  fontFamily: "Rethink Sans, sans-serif",
  display: "flex",
  alignItems: "center",
  gap: "8px",
});

const PayorSectionIcon = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "20px",
  height: "20px",
  flexShrink: 0,
});

const ACCORDION_SUMMARY_SX = {
  alignItems: "center",
  borderRadius: "4px",
  border: "1px solid #9EE5F4",
  backgroundColor: "#FFF",
  boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(180deg)",
  },
} as const;

const EXPAND_CHEVRON = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden
  >
    <path
      d="M5 7.5L10 12.5L15 7.5"
      stroke="#262626"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PAYOR_FILE_RE = /\.(pdf|docx|doc|txt|xlsx)$/i;

/** User-visible labels for payer contract analysis `package_status` values. */
const PAYOR_CONTRACT_PACKAGE_STATUS_LABELS: Record<string, string> = {
  FULL_ASSESSMENT_ALLOWED: "Ready",
  LIMITED_ASSESSMENT_ONLY: "Limited review",
  INSUFFICIENT_DOCUMENTS_HARD_STOP: "Incomplete documents",
};

function payorContractPackageStatusLabel(status: unknown): string {
  if (status === null || status === undefined || status === "") return "—";
  const key = String(status);
  return PAYOR_CONTRACT_PACKAGE_STATUS_LABELS[key] ?? key;
}

function filterPayorContractFiles(files: File[]): File[] {
  return files.filter((f) => PAYOR_FILE_RE.test(f.name));
}

function metadataForPayorContractHistory(
  metadata: PayorContractAnalysisResponse["metadata"] | undefined,
): PayorContractAnalysisResponse["metadata"] {
  const base = metadata ? { ...metadata } : {};
  const fr = base.file_results;
  if (!Array.isArray(fr)) return base;
  return {
    ...base,
    file_results: fr.map((row) => {
      const { extracted_text: _omit, ...rest } = row;
      return rest;
    }),
  };
}

export interface PayorContractAnalysisProps {
  facilityData?: unknown;
  onWatchGuideClick?: () => void;
  watchGuideLoading?: boolean;
  watchGuideIconSrc?: string;
  showWatchGuide?: boolean;
  /** When set, coordinates expand/collapse with other Risk Analysis tab accordions. */
  accordionExpanded?: boolean;
  onAccordionChange?: (
    event: React.SyntheticEvent,
    isExpanded: boolean,
  ) => void;
}

export default function PayorContractAnalysis({
  facilityData,
  onWatchGuideClick,
  watchGuideLoading = false,
  watchGuideIconSrc,
  showWatchGuide = false,
  accordionExpanded,
  onAccordionChange,
}: PayorContractAnalysisProps) {
  type ResolvedFacility = {
    facility_id?: number;
    state?: string | null;
    facility_type?: string | null;
    facility_type_slug?: string | null;
    vertical?: string | null;
  } & AviationProfileFacilityLike;

  const resolvedFacility = useMemo(() => {
    const f = facilityData || getSelectedFacilityFromSession();
    return f as ResolvedFacility | null;
  }, [facilityData]);

  const stateFromSelectedFacility = useMemo(() => {
    const s = resolvedFacility?.state;
    return typeof s === "string" ? s.trim() : "";
  }, [resolvedFacility]);

  const facilityTypeFromSelectedFacility = useMemo(() => {
    const f = resolvedFacility;
    if (!f) return "";
    if ((f.vertical || "").toLowerCase() === "aviation") {
      return getAviationProfileLabel(f).trim();
    }
    const name = (f.facility_type || "").trim();
    if (name) return name;
    const slug = (f.facility_type_slug || "").trim();
    return slug ? slug.replace(/_/g, " ") : "";
  }, [resolvedFacility]);

  const [states, setStates] = useState("");

  useEffect(() => {
    setStates(stateFromSelectedFacility);
  }, [resolvedFacility?.facility_id, stateFromSelectedFacility]);

  const [files, setFiles] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<PayorContractAnalysisResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [partialFileResults, setPartialFileResults] = useState<
    EmployeeAgreementFileResult[] | null
  >(null);
  const [errorFileResults, setErrorFileResults] =
    useState<EmployeeAgreementFileResult[] | null>(null);
  const [extractingFilenames, setExtractingFilenames] = useState<Set<string>>(
    () => new Set(),
  );
  const [pdfHintByFilename, setPdfHintByFilename] = useState<
    Record<string, boolean>
  >({});
  const [filePageProgress, setFilePageProgress] = useState<
    Record<string, { page: number; total: number }>
  >({});
  const payorPageProgRef = useRef<{
    pending: Record<string, { page: number; total: number }>;
    timer: ReturnType<typeof setTimeout> | null;
    lastEmit: number;
  }>({ pending: {}, timer: null, lastEmit: 0 });

  const [historyRows, setHistoryRows] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyRefreshCounter, setHistoryRefreshCounter] = useState(0);
  const [historyDeletingId, setHistoryDeletingId] = useState<number | null>(null);
  const [historySavedForCurrentRun, setHistorySavedForCurrentRun] =
    useState(false);
  const [historyPersisting, setHistoryPersisting] = useState(false);
  /** History row id when the current result was opened from Recent Analysis; null for a fresh run. */
  const [activeHistoryId, setActiveHistoryId] = useState<string | number | null>(null);
  /** ISO (or API) datetime string used to label the current results panel. */
  const [analysedAtSource, setAnalysedAtSource] = useState<string | null>(null);

  const showTabbedOutput =
    loading ||
    (Boolean(result?.success) && Boolean(result?.analysis)) ||
    Boolean(errorFileResults && errorFileResults.length > 0);

  const historyCountryCode = getSelectedCountryCode();

  const schedulePayorPageProgress = useCallback(
    (ev: { filename: string; page: number; totalPages: number }) => {
      const refBox = payorPageProgRef.current;
      refBox.pending[ev.filename] = { page: ev.page, total: ev.totalPages };
      const throttleMs = 400;
      const now = Date.now();
      const flush = () => {
        if (!Object.keys(refBox.pending).length) return;
        const snap = { ...refBox.pending };
        refBox.pending = {};
        refBox.lastEmit = Date.now();
        setFilePageProgress((prev) => ({ ...prev, ...snap }));
      };
      if (now - refBox.lastEmit >= throttleMs) {
        if (refBox.timer) {
          clearTimeout(refBox.timer);
          refBox.timer = null;
        }
        flush();
        return;
      }
      if (!refBox.timer) {
        refBox.timer = setTimeout(() => {
          refBox.timer = null;
          flush();
        }, Math.max(40, throttleMs - (now - refBox.lastEmit)));
      }
    },
    [],
  );

  const statusFileResults = useMemo(() => {
    if (loading && files.length > 0) {
      return buildMergedExtractionFileResults(
        files,
        extractingFilenames,
        partialFileResults,
        {
          pdfHintByFilename,
          pageByFilename: filePageProgress,
        },
      );
    }
    if (loading) return null;
    return (
      result?.metadata?.file_results ??
      errorFileResults ??
      null
    );
  }, [
    loading,
    files,
    extractingFilenames,
    partialFileResults,
    pdfHintByFilename,
    filePageProgress,
    result?.metadata?.file_results,
    errorFileResults,
  ]);

  const persistResultToHistory = useCallback(
    async (
      streamResult: PayorContractAnalysisResponse,
      sortedFiles: File[],
    ): Promise<
      | { ok: true }
      | { ok: false; skipped?: boolean; errorMessage: string }
    > => {
      const userIdStr = getCurrentUserId();
      const userId = userIdStr ? parseInt(userIdStr, 10) : 0;
      const facilityNumericId = Number(resolvedFacility?.facility_id) || 0;
      if (!userId || !facilityNumericId) {
        return {
          ok: false,
          skipped: true,
          errorMessage:
            "Analysis was not saved to history (missing user or facility).",
        };
      }
      const totalBytes = sortedFiles.reduce((s, f) => s + f.size, 0);
      return continuousComplianceService.saveAnalysisHistory({
        user_id: userId,
        facility_id: facilityNumericId,
        type: "Payor contract analysis",
        data_obj: {
          analysis: streamResult.analysis,
          metadata: {
            ...metadataForPayorContractHistory(streamResult.metadata),
            filename: sortedFiles.map((f) => f.name).join("; "),
            filenames: sortedFiles.map((f) => f.name),
            file_count: sortedFiles.length,
            file_size: totalBytes,
          },
        },
      });
    },
    [resolvedFacility?.facility_id],
  );

  const saveCurrentRunToHistory = useCallback(async () => {
    if (!result?.success || !result.analysis || !files.length) {
      toast.warning("Run an analysis with uploaded files before saving.");
      return;
    }
    setHistoryPersisting(true);
    try {
      const outcome = await persistResultToHistory(result, files);
      if (outcome.ok) {
        setHistoryRefreshCounter((c) => c + 1);
        setHistorySavedForCurrentRun(true);
        toast.success("Saved to recent analysis.");
      } else if (outcome.skipped) {
        toast.warning(outcome.errorMessage);
      } else {
        toast.error(`Could not save: ${outcome.errorMessage}`);
      }
    } finally {
      setHistoryPersisting(false);
    }
  }, [result, files, persistResultToHistory]);

  const loadHistory = useCallback(async () => {
    const facilityId = resolvedFacility?.facility_id;
    const userIdStr = getCurrentUserId();
    const userId = userIdStr ? parseInt(userIdStr, 10) : 0;
    if (!facilityId || !userId) return;
    setHistoryLoading(true);
    try {
      const res = await continuousComplianceService.getAnalysisHistory({
        user_id: userId,
        facility_id: facilityId,
        type: "Payor contract analysis",
        page: 1,
        page_size: 10,
      });
      setHistoryRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error("Payor contract history fetch failed", e);
      setHistoryRows([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [resolvedFacility?.facility_id]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory, historyRefreshCounter]);

  const clearAnalysis = useCallback(() => {
    setFiles([]);
    setResult(null);
    setError(null);
    setErrorFileResults(null);
    setPartialFileResults(null);
    setExtractingFilenames(new Set());
    setPdfHintByFilename({});
    setFilePageProgress({});
    payorPageProgRef.current = { pending: {}, timer: null, lastEmit: 0 };
    setDragOver(false);
    setActiveHistoryId(null);
    setAnalysedAtSource(null);
    setHistorySavedForCurrentRun(false);
  }, []);

  const handleDeleteHistoryRow = useCallback(
    async (rowId: number) => {
      if (
        !window.confirm(
          "Remove this analysis from Recent Analysis? This cannot be undone.",
        )
      ) {
        return;
      }
      setHistoryDeletingId(rowId);
      try {
        await continuousComplianceService.deleteAnalysisHistoryEntry(rowId);
        if (activeHistoryId === rowId) {
          clearAnalysis();
        }
        setHistoryRefreshCounter((c) => c + 1);
        toast.success("Analysis removed from your recent list.");
      } catch (e) {
        console.error("Payor contract history delete failed", e);
        toast.error(
          "Could not delete this analysis. Please try again.",
        );
      } finally {
        setHistoryDeletingId(null);
      }
    },
    [activeHistoryId, clearAnalysis],
  );

  const analysedAtDisplay = analysedAtSource
    ? formatDateTime(analysedAtSource, historyCountryCode)
    : null;

  const handleUpload = async (picked: File[]) => {
    if (!picked.length) {
      setError("No supported files (.pdf, .docx, .doc, .txt, .xlsx) in the selection.");
      return;
    }
    const sorted = sortPayorContractFilesForAnalysis(picked);
    setFiles(sorted);
    setLoading(true);
    setError(null);
    setErrorFileResults(null);
    setPartialFileResults([]);
    setExtractingFilenames(new Set());
    setPdfHintByFilename({});
    setFilePageProgress({});
    payorPageProgRef.current = { pending: {}, timer: null, lastEmit: 0 };
    setResult(null);
    setActiveHistoryId(null);
    setAnalysedAtSource(null);
    setHistorySavedForCurrentRun(false);
    try {
      const facilityId = resolvedFacility?.facility_id?.toString();
      if (!facilityId) {
        throw new Error("Please select a facility before analysis.");
      }
      const streamResult = await riskAnalysisService.analyzePayorContractStream(
        sorted,
        facilityId,
        {
          country: getSelectedCountryCode(),
          states,
          facilityTypes: facilityTypeFromSelectedFacility,
        },
        {
          onFileExtracting: (fn, meta) => {
            setExtractingFilenames((prev) => new Set(prev).add(fn));
            setPdfHintByFilename((prev) => ({
              ...prev,
              [fn]: Boolean(
                meta?.pdf ?? fn.toLowerCase().endsWith(".pdf"),
              ),
            }));
            setFilePageProgress((prev) => {
              const next = { ...prev };
              delete next[fn];
              return next;
            });
          },
          onFilePageProgress: (ev) => {
            schedulePayorPageProgress(ev);
          },
          onFileResult: (fr) => {
            setExtractingFilenames((prev) => {
              const next = new Set(prev);
              next.delete(fr.filename);
              return next;
            });
            setPartialFileResults((prev) => {
              const base = prev ?? [];
              const idx = base.findIndex((x) => x.filename === fr.filename);
              if (idx >= 0) {
                const next = [...base];
                next[idx] = fr;
                return next;
              }
              return [...base, fr];
            });
          },
          onComplete: (r) => {
            setExtractingFilenames(new Set());
            setResult(r);
            setErrorFileResults(null);
            setPartialFileResults(null);
            setAnalysedAtSource(new Date().toISOString());
          },
          onError: (message, fileResults) => {
            setExtractingFilenames(new Set());
            setError(message);
            setErrorFileResults(
              fileResults && fileResults.length ? fileResults : null,
            );
            setPartialFileResults(null);
          },
        },
      );
      if (streamResult) {
        const outcome = await persistResultToHistory(streamResult, sorted);
        if (outcome.ok) {
          setHistoryRefreshCounter((c) => c + 1);
          setHistorySavedForCurrentRun(true);
        } else if (outcome.skipped) {
          toast.warning(outcome.errorMessage);
        } else {
          toast.error(
            `Payor contract analysis could not be saved to history: ${outcome.errorMessage}`,
          );
        }
      }
    } catch (err) {
      setExtractingFilenames(new Set());
      setPartialFileResults(null);
      if (err instanceof PayorContractRequestError) {
        setError(err.message);
        setErrorFileResults(
          err.fileResults && err.fileResults.length ? err.fileResults : null,
        );
      } else {
        setError(err instanceof Error ? err.message : String(err));
        setErrorFileResults(null);
      }
    } finally {
      setExtractingFilenames(new Set());
      setLoading(false);
    }
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const raw = Array.from(e.dataTransfer.files || []);
    void handleUpload(filterPayorContractFiles(raw));
  };

  return (
    <PayorSectionAccordion
      expanded={accordionExpanded}
      onChange={onAccordionChange}
    >
      <AccordionSummary
        expandIcon={EXPAND_CHEVRON}
        aria-controls="payor-contract-content"
        id="payor-contract-header"
        sx={ACCORDION_SUMMARY_SX}
      >
        <PayorSectionIcon aria-hidden>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              d="M5.5 3.5H11.5L14.5 6.5V16.5C14.5 17.0523 14.0523 17.5 13.5 17.5H5.5C4.94772 17.5 4.5 17.0523 4.5 16.5V4.5C4.5 3.94772 4.94772 3.5 5.5 3.5Z"
              stroke="#262626"
              strokeWidth="1.25"
              strokeLinejoin="round"
            />
            <path
              d="M11.5 3.5V6.5H14.5"
              stroke="#262626"
              strokeWidth="1.25"
              strokeLinejoin="round"
            />
            <path
              d="M6.5 9.5H12.5M6.5 12H12.5"
              stroke="#262626"
              strokeWidth="1.25"
              strokeLinecap="round"
            />
          </svg>
        </PayorSectionIcon>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "nowrap",
            gap: 1,
            flex: 1,
            minWidth: 0,
          }}
        >
          <PayorSectionTitle className="accordion-title" sx={{ lineHeight: 1.25 }}>
            Payor Contract
          </PayorSectionTitle>
        </Box>
        {showWatchGuide ? (
          <Button
            variant="outlined"
            disabled={!onWatchGuideClick || watchGuideLoading}
            onClick={(e) => {
              e.stopPropagation();
              onWatchGuideClick?.();
            }}
            sx={{
              display: "flex",
              flexShrink: 0,
              minHeight: "40px",
              padding: "8px 16px",
              justifyContent: "center",
              alignItems: "center",
              gap: "8px",
              borderRadius: "8px",
              border: "1px solid #5BBFD6",
              background: "#FFFFFF",
              boxShadow: "0 2px 2px 0 rgba(42, 116, 131, 0.20)",
              textTransform: "none",
              marginRight: "8px",
              "&:hover": {
                backgroundColor: "#F8FDFF",
                borderColor: "#3CA9C2",
              },
            }}
          >
            {watchGuideIconSrc ? (
              <Box
                component="img"
                src={watchGuideIconSrc}
                alt=""
                sx={{ width: 16, height: 16, display: "block" }}
              />
            ) : null}
            <Typography
              sx={{
                color: "#086E86",
                fontFamily: '"Rethink Sans", sans-serif',
                fontSize: "13px",
                fontWeight: 600,
                lineHeight: "normal",
              }}
            >
              {watchGuideLoading ? "Loading Guide..." : "Watch Guide"}
            </Typography>
          </Button>
        ) : null}
      </AccordionSummary>
      <AccordionDetails
        sx={{
          backgroundColor: "#ffffff",
          padding: "12px",
        }}
      >
      <Typography
        sx={{
          mb: 2,
          mt:1,
          fontSize: "12px",
          color: "#64748b",
          fontFamily: "Rethink Sans, sans-serif",
        }}
      >
        AI-assisted review for contracting and revenue-cycle teams. Not legal advice.
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 1.5,
          mb: 2,
        }}
      >
        <TextField
          label="Facility type (from selected facility)"
          size="small"
          value={facilityTypeFromSelectedFacility}
          slotProps={{
            input: { readOnly: true },
            htmlInput: { "aria-readonly": true as const },
          }}
          sx={{
            minWidth: 0,
            "& .MuiInputBase-input": { cursor: "default" },
          }}
        />
        <TextField
          label="State / province"
          size="small"
          value={states}
          onChange={(e) => setStates(e.target.value)}
          sx={{ minWidth: 0 }}
        />
      </Box>

      <Box
        sx={{
          border: dragOver ? "2px dashed #0a829d" : "1px dashed #5bbfd6",
          borderRadius: "10px",
          p: 2,
          mb: 2,
          backgroundColor: dragOver ? "#f0fcff" : "#fafdfe",
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragEnter={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setDragOver(false);
        }}
        onDrop={onDrop}
      >
        {!showTabbedOutput ? (
          <Box sx={{ textAlign: "center" }}>
            <Typography sx={{ fontFamily: "Rethink Sans, sans-serif", mb: 1 }}>
              Drag and drop payer contract documents, or{" "}
              <Box
                component="span"
                role="button"
                tabIndex={0}
                onClick={() =>
                  document.getElementById("payor-contract-file-input")?.click()
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    document.getElementById("payor-contract-file-input")?.click();
                  }
                }}
                sx={{
                  color: "#0a829d",
                  cursor: "pointer",
                  fontWeight: 700,
                  textDecoration: "underline",
                }}
              >
                browse
              </Box>
            </Typography>
            <input
              id="payor-contract-file-input"
              type="file"
              accept=".pdf,.docx,.doc,.txt,.xlsx"
              multiple
              style={{ display: "none" }}
              onChange={(e) => {
                const list = e.target.files ? Array.from(e.target.files) : [];
                e.target.value = "";
                if (list.length) void handleUpload(filterPayorContractFiles(list));
              }}
            />
          </Box>
        ) : null}
        {loading ? (
          <Box sx={{ mt: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={22} />
              <Typography fontFamily="Rethink Sans, sans-serif">
                Processing…
              </Typography>
            </Box>
            {files.length > 0 && statusFileResults ? (
              <ExtractionProgressTable rows={statusFileResults} />
            ) : null}
          </Box>
        ) : null}
      </Box>

      {showTabbedOutput ? (
        <PayorContractResults
          analysis={
            result?.success && result.analysis
              ? (result.analysis as Record<string, unknown>)
              : null
          }
          loading={loading}
          statusFiles={files}
          statusFileResults={statusFileResults}
          analysedAtDisplay={analysedAtDisplay}
        />
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      ) : null}

      <PayorSectionAccordion
        sx={{
          mt: 1,
          "&.Mui-expanded .MuiAccordionSummary-root": {
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
          },
          "& .MuiAccordionDetails-root": {
            padding: "0 !important",
          },
        }}
      >
        <AccordionSummary
          expandIcon={EXPAND_CHEVRON}
          aria-controls="payor-recent-analysis-content"
          id="payor-recent-analysis-header"
          sx={ACCORDION_SUMMARY_SX}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              gap: 1,
            }}
          >
            <Typography fontWeight={600} fontFamily="Rethink Sans, sans-serif">
              Recent Analysis
            </Typography>
            <Typography
              component="span"
              fontFamily="Rethink Sans, sans-serif"
              fontWeight={600}
              color="text.secondary"
              sx={{ flexShrink: 0 }}
            >
              {historyLoading ? "—" : `(${historyRows.length})`}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {historyLoading ? (
            <Box sx={{ py: 1.5, px: 1.5 }}>
              <CircularProgress size={22} />
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{
                borderTop: "none",
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
                boxShadow: "none",
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Uploaded</TableCell>
                    <TableCell>Package status</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3}>No saved analyses yet.</TableCell>
                    </TableRow>
                  ) : (
                    historyRows.map((row, historyIdx) => {
                      const analysisObj = row?.data_obj?.analysis as
                        | Record<string, unknown>
                        | undefined;
                      const ps = analysisObj?.package_status;
                      const created = row?.created_at || row?.updated_at || "";
                      const uploadedDisplay = created
                        ? formatDateTime(String(created), historyCountryCode)
                        : "—";
                      const rowId = row?.id;
                      const isActiveView =
                        rowId != null && activeHistoryId != null && rowId === activeHistoryId;
                      return (
                        <TableRow
                          key={rowId != null ? String(rowId) : `hist-${historyIdx}`}
                          sx={
                            isActiveView
                              ? {
                                  bgcolor: "rgba(10, 130, 157, 0.08)",
                                  boxShadow: "inset 3px 0 0 #0a829d",
                                  "& > td": { fontWeight: 600 },
                                }
                              : undefined
                          }
                        >
                          <TableCell>{uploadedDisplay}</TableCell>
                          <TableCell>{payorContractPackageStatusLabel(ps)}</TableCell>
                          <TableCell align="right">
                            <Stack
                              direction="row"
                              spacing={0.5}
                              justifyContent="flex-end"
                              alignItems="center"
                            >
                              <Button
                                size="small"
                                onClick={() => {
                                  const a = row?.data_obj?.analysis;
                                  const m = row?.data_obj?.metadata;
                                  if (a) {
                                    setResult({
                                      success: true,
                                      analysis: a as Record<string, unknown>,
                                      metadata: (m || {}) as PayorContractAnalysisResponse["metadata"],
                                    });
                                    setFiles([]);
                                    setError(null);
                                    if (rowId != null) setActiveHistoryId(rowId);
                                    const ts = row?.created_at || row?.updated_at;
                                    setAnalysedAtSource(ts ? String(ts) : null);
                                  }
                                }}
                              >
                                View
                              </Button>
                              {typeof rowId === "number" ? (
                                <Button
                                  size="small"
                                  color="error"
                                  disabled={historyDeletingId === rowId}
                                  onClick={() => void handleDeleteHistoryRow(rowId)}
                                >
                                  {historyDeletingId === rowId ? "…" : "Delete"}
                                </Button>
                              ) : null}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </AccordionDetails>
      </PayorSectionAccordion>

      {showTabbedOutput ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            mt: 1.5,
          }}
        >
          {result?.success &&
          result.analysis &&
          !activeHistoryId &&
          files.length > 0 ? (
            <Button
              variant="contained"
              size="small"
              disabled={historyPersisting || historySavedForCurrentRun}
              onClick={() => void saveCurrentRunToHistory()}
              sx={{ fontFamily: "Rethink Sans, sans-serif" }}
            >
              {historyPersisting
                ? "Saving…"
                : historySavedForCurrentRun
                  ? "Saved to recent analysis"
                  : "Save to recent analysis"}
            </Button>
          ) : null}
          <Button
            variant="outlined"
            size="small"
            onClick={clearAnalysis}
            sx={{ fontFamily: "Rethink Sans, sans-serif" }}
          >
            New analysis
          </Button>
        </Box>
      ) : null}
      </AccordionDetails>
    </PayorSectionAccordion>
  );
}
